/**
 * AzureResourcePicker — dialog listing a signed-in Azure account's discoverable
 * resources (subscription → kind → resource tree), mapping a pick to a
 * connection draft. Three modes: single-select (`onPick`: the form's Browse
 * Azure, "Use resource"), `multiSelect` (`onPickMany`: the import source,
 * "Import N connections…"), and `entryMode` (Option C, C3.1: the New Connection
 * dialog's top-level Browse Azure) — multi-select with a dynamic confirm: one
 * checked, or Enter / a double-click on a row → `onPick` ("Open in form"); two
 * or more → `onPickMany` ("Review N connections…"). `onCloseAutoFocus` tells the
 * caller how the picker closed so it can place focus (a pick: the form's first
 * required empty field; Cancel: the entry that opened it). Owns search,
 * selection/focus, the Managed Instance endpoint choice and the pick / error /
 * partial analytics; the identity-safe query, the identity actions, tree, chip,
 * body, error copy and filter helpers live in sibling files.
 *
 * Confirm is enabled only for rows the CURRENT principal owns
 * (`useAzureDiscovery().confirmable`): never during identity verification, never
 * after an account change, never for rows of a previous generation.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type {
  AzureDiscoveredResource,
  AzureDiscoveryEngine,
  AzureIdentitySource,
  AzureManagedInstanceEndpoint,
} from '../../../types/azureDiscovery';
import { withEndpointChoice } from '../../../types/azureDiscovery';
import { safeServerText } from '../../../types/safeConnectionDisplay';
import { AzureIdentityChip } from './AzureIdentityChip';
import { AzurePickerBody } from './AzurePickerBody';
import { AzurePickerEndpointChoice } from './AzurePickerEndpointChoice';
import { buildTree, filterTree } from './azurePickerFilter';
import { useAzureDiscovery } from './useAzureDiscovery';
import { useAzurePickerIdentityActions } from './useAzurePickerIdentityActions';
import { useAzurePickerSelection } from './useAzurePickerSelection';
import { useAzurePickerStore } from './azurePickerStore';
import { FUNNEL_VERSION } from '../connection-dialog/hooks/useConnectionDialogFunnel';

/** Where the picker was opened from (`feature:azure_browse_open.entry`; low-cardinality, no PII). */
export type AzurePickerEntry = 'form' | 'import' | 'start' | 'rail' | 'compact';

/** How the picker closed: `picked` (one resource, `onPick`), `review` (several, `onPickMany`) or `cancelled`. */
export type AzurePickerCloseOutcome = 'picked' | 'review' | 'cancelled';

export interface AzureResourcePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engineFilter?: AzureDiscoveryEngine;
  multiSelect?: boolean;
  /** The New Connection dialog's entry (C3.1): multi-select tree, one pick → `onPick`, several → `onPickMany`. */
  entryMode?: boolean;
  entry?: AzurePickerEntry;
  onPick?: (resource: AzureDiscoveredResource) => void;
  onPickMany?: (resources: AzureDiscoveredResource[]) => void;
  disabledResourceIds?: string[];
  /** Radix's close auto-focus, with how the picker closed; `preventDefault` keeps focus where the caller put it. */
  onCloseAutoFocus?: (event: Event, outcome: AzurePickerCloseOutcome) => void;
}

export function AzureResourcePicker(props: AzureResourcePickerProps) {
  const { open, onOpenChange, engineFilter, entryMode = false, disabledResourceIds } = props;
  const multiSelect = entryMode || props.multiSelect === true;
  const entry: AzurePickerEntry = props.entry ?? (multiSelect ? 'import' : 'form');
  const { track } = useAnalytics();
  const preferredIdentity = useAzurePickerStore((s) => s.preferredIdentity);

  const [activeSource, setActiveSource] = useState<AzureIdentitySource | undefined>(preferredIdentity);
  const [search, setSearch] = useState('');
  const [endpointChoices, setEndpointChoices] = useState<Record<string, AzureManagedInstanceEndpoint>>({});

  const discovery = useAzureDiscovery({ open, source: activeSource });
  const { data, identity, isLoading, isFetching, refetch, setInteractive, ownerMismatch, confirmable, confirmNow, invalidateConfirmations } = discovery;
  const [confirming, setConfirming] = useState(false);
  // A pick is delivered only while the picker is still open and mounted when its check comes back (r2 issue 1).
  const liveRef = useRef(open);
  useEffect(() => {
    liveRef.current = open;
    if (!open) invalidateConfirmations();
    return () => {
      liveRef.current = false;
    };
  }, [open, invalidateConfirmations]);
  const errorResult = discovery.identityError ?? (data && !data.ok ? data.error : undefined);
  const completeness = data?.ok ? data.completeness : undefined;
  const analyticsEngine = engineFilter ?? 'all';
  const resources = useMemo(() => (data?.ok ? data.resources : []), [data]);
  // Selection/focus live in the hook, which also prunes both whenever the
  // resource set is replaced (identity change, Refresh) — see its doc block.
  const { selectedIds, focusedId, setFocusedId, applySelection, toggleResource, resetSelection } =
    useAzurePickerSelection(resources);

  const openedRef = useRef(false);
  useEffect(() => {
    if (open && !openedRef.current) {
      openedRef.current = true;
      track('feature:azure_browse_open', { engine: analyticsEngine, entry, funnel_version: FUNNEL_VERSION });
    }
    if (!open) {
      openedRef.current = false;
      setSearch('');
      resetSelection();
    }
  }, [open, analyticsEngine, entry, track, resetSelection]);

  // One event per error shown and per incomplete list (per reply object). Codes and reasons only — no text.
  const trackedError = useRef<unknown>(undefined);
  useEffect(() => {
    if (!errorResult || trackedError.current === errorResult || errorResult.code === 'cancelled') return;
    trackedError.current = errorResult;
    track('feature:azure_browse_error', { code: errorResult.code });
  }, [errorResult, track]);
  const trackedPartial = useRef<unknown>(undefined);
  useEffect(() => {
    if (!completeness || completeness.complete || trackedPartial.current === completeness) return;
    trackedPartial.current = completeness;
    track('feature:azure_browse_partial', { reason: completeness.reason ?? 'batch_failed' });
  }, [completeness, track]);

  const disabledIds = useMemo(() => new Set(disabledResourceIds ?? []), [disabledResourceIds]);
  const fullTree = useMemo(() => buildTree(resources, engineFilter), [resources, engineFilter]);
  const visibleTree = useMemo(() => filterTree(fullTree, search), [fullTree, search]);
  const focusedResource = resources.find((r) => r.id === focusedId);
  const endpointOf = (resource: AzureDiscoveredResource): AzureManagedInstanceEndpoint => endpointChoices[resource.id] ?? 'vnet';
  const applyChoice = (resource: AzureDiscoveredResource) => withEndpointChoice(resource, endpointOf(resource));

  const { effectiveSource, signInError, switchTo, refresh, signInEntra, signOut } = useAzurePickerIdentityActions({
    activeSource,
    setActiveSource,
    resolvedSource: identity?.source,
    setInteractive,
    refetch,
    track,
  });
  /** Every confirmation path (button, Enter, double-click, multi-select) re-resolves the identity first (C4A-01). */
  const verifiedBeforeConfirm = async (): Promise<boolean> => {
    if (!confirmable || confirming) return false;
    setConfirming(true);
    try {
      return (await confirmNow()) && liveRef.current;
    } finally {
      if (liveRef.current) setConfirming(false);
    }
  };
  // How the picker is closing, for `onCloseAutoFocus` (Radix fires it after the close).
  const outcomeRef = useRef<AzurePickerCloseOutcome>('cancelled');
  const confirmSingle = async (resource: AzureDiscoveredResource) => {
    if (!(await verifiedBeforeConfirm())) return;
    const chosen = applyChoice(resource);
    track('feature:azure_browse_pick', { engine: chosen.engine, kind: chosen.kind, endpoint: endpointOf(resource), action: entryMode ? 'fill_form' : 'use', funnel_version: FUNNEL_VERSION });
    outcomeRef.current = 'picked';
    props.onPick?.(chosen);
    onOpenChange(false);
  };
  const confirmMany = async () => {
    if (!(await verifiedBeforeConfirm())) return;
    const picked = resources.filter((r) => selectedIds.has(r.id)).map(applyChoice);
    const counts = new Map<string, { engine: AzureDiscoveryEngine; count: number }>();
    for (const r of picked) {
      const entry = counts.get(r.kind) ?? { engine: r.engine, count: 0 };
      entry.count += 1;
      counts.set(r.kind, entry);
    }
    for (const [kind, { engine, count }] of counts) track('feature:azure_browse_pick', { engine, kind, count, action: entryMode ? 'review' : 'import', funnel_version: FUNNEL_VERSION });
    outcomeRef.current = 'review';
    props.onPickMany?.(picked);
    onOpenChange(false);
  };
  /** Entry mode's confirm button: the one checked resource opens in the form; two or more go to the review. */
  const confirmEntry = () => {
    if (selectedIds.size >= 2) return confirmMany();
    const only = resources.find((r) => selectedIds.has(r.id)) ?? focusedResource;
    return only ? confirmSingle(only) : undefined;
  };
  const changeEndpoint = (resource: AzureDiscoveredResource, endpoint: AzureManagedInstanceEndpoint) => {
    if (endpointOf(resource) === endpoint) return;
    track('feature:azure_browse_endpoint', { endpoint });
    setEndpointChoices((prev) => ({ ...prev, [resource.id]: endpoint }));
  };

  const chip = (
    <AzureIdentityChip
      identity={identity}
      activeSource={effectiveSource}
      onSwitch={switchTo}
      onSignInDifferent={() => void signInEntra(false)}
      onSignOut={() => void signOut()}
    />
  );

  const confirmDisabled = !confirmable || confirming || (entryMode ? selectedIds.size === 0 && !focusedResource : multiSelect ? selectedIds.size === 0 : !focusedResource);
  const confirmLabel = entryMode
    ? selectedIds.size >= 2 ? `Review ${selectedIds.size} connections…` : 'Open in form'
    : multiSelect ? `Import ${selectedIds.size} connection${selectedIds.size === 1 ? '' : 's'}…` : 'Use resource';
  const confirm = () => void (entryMode ? confirmEntry() : multiSelect ? confirmMany() : focusedResource && confirmSingle(focusedResource));
  const previewResource = focusedResource ? applyChoice(focusedResource) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] flex-col sm:max-w-[640px]"
        data-testid="azure-picker-dialog"
        data-picker-mode={entryMode ? 'entry' : multiSelect ? 'multi' : 'single'}
        onCloseAutoFocus={(event) => props.onCloseAutoFocus?.(event, outcomeRef.current)}
      >
        <DialogHeader className="flex-row items-center justify-between gap-2 space-y-0 pr-8">
          <DialogTitle>Browse Azure</DialogTitle>
          <div className="flex items-center gap-2">
            {chip}
            <Button variant="outline" size="sm" className="h-8 gap-1.5 px-2 text-xs" onClick={refresh} data-testid="azure-picker-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
              Refresh
            </Button>
          </div>
        </DialogHeader>

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search resources, resource groups, subscriptions…"
          aria-label="Search Azure resources"
          data-testid="azure-picker-search"
        />

        <div className="min-h-[280px] flex-1">
          <AzurePickerBody
            isLoading={isLoading}
            error={signInError ?? errorResult}
            identity={identity}
            completeness={completeness}
            ownerMismatch={ownerMismatch}
            fullTreeEmpty={fullTree.length === 0}
            visibleTree={visibleTree}
            search={search}
            multiSelect={multiSelect}
            confirmOnRow={entryMode}
            selectedIds={selectedIds}
            focusedId={focusedId}
            disabledIds={disabledIds}
            chip={chip}
            onSignIn={() => void signInEntra(true)}
            onSignInDifferent={() => void signInEntra(false)}
            onRetry={refresh}
            onToggleResource={toggleResource}
            onToggleMany={applySelection}
            onFocus={(r) => setFocusedId(r.id)}
            onConfirmResource={(resource) => void confirmSingle(resource)}
          />
        </div>

        <div className="flex flex-col gap-2 border-t pt-3">
          {focusedResource ? (
            <AzurePickerEndpointChoice resource={focusedResource} value={endpointOf(focusedResource)} onChange={(v) => changeEndpoint(focusedResource, v)} />
          ) : null}
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" data-testid="azure-picker-preview">
              {previewResource
                ? `${safeServerText(previewResource.connectionDraft.server, previewResource.engine)}${previewResource.endpointChoices ? `:${previewResource.connectionDraft.port}` : ''}`
                : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  invalidateConfirmations();
                  outcomeRef.current = 'cancelled';
                  onOpenChange(false);
                }}
                data-testid="azure-picker-cancel"
              >
                Cancel
              </Button>
              <Button
                disabled={confirmDisabled}
                onClick={confirm}
                data-testid="azure-picker-confirm"
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
